/*!
 * This module handles assigning cards to epics.
 */
import { type MutableRefObject, useState, useEffect } from 'react';
import uniqBy from 'lodash/uniqBy';
import type { ApolloError } from 'apollo-client';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { isClientFetchError } from '@atlassian/jira-fetch/src/utils/is-error.tsx';
import { useFlagsService } from '@atlassian/jira-flags'; // ignore-for-ENGHEALTH-17759
import { useIntl } from '@atlassian/jira-intl';
import { ISSUE_HIERARCHY_LEVEL_BASE } from '@atlassian/jira-issue-type-hierarchies/src/index.tsx';
import {
	fireTrackAnalytics,
	useAnalyticsEvents,
	fireOperationalAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { useAutobatch } from '@atlassian/jira-software-react-scheduler/src/ui/autobatch/index.tsx';
import { useEvent } from '@atlassian/jira-software-react-use-event/src/index.tsx';
import { getUpdateAnalyticsFlowHelper } from '@atlassian/jira-issue-analytics/src/services/update-issue-field/index.tsx';
import { PARENT_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import {
	DRAG_DROP_UNKNOWN_FIELD_ID,
	FIELD_TYPE_MAP,
} from '@atlassian/jira-issue-analytics/src/services/update-issue-field/constants.tsx';
import {
	DIVIDER_DRAGGABLE_ID_PREFIX,
	ANALYTICS_SOURCE_EPIC_PANEL,
	UNASSIGNED_EPIC_ID,
	DND_OPERATION_CARD_PARENT_UPDATE,
} from '../../../../../../common/constants.tsx';
import type { DndOperation, SelectedIssue } from '../../../../../../common/types.tsx';
import { useAssignEpicMutation } from '../../../../../../services/epic/assign/index.tsx';
import { useUnassignEpicMutation } from '../../../../../../services/epic/unassign/index.tsx';
import { useUpdateEpicParentMutation } from '../../../../../../services/epic/update-parent/index.tsx';
import { useUpdateEpicPanelExperienceTracker } from '../../../../../../services/experience-tracker/index.tsx';
import {
	cancelBacklogPanelEpicUpdateInteraction,
	setBacklogPanelEpicUpdateInteractionStartMark,
	stopBacklogPanelEpicUpdateInteraction,
} from '../../../../../../services/performance/metrics-interaction-epic-update/index.tsx';
import messages from './messages.tsx';
import { useDropToAssignExperienceTracker, getDropToAssignMarks } from './utils.tsx';

export const isDraggableDivider = (draggableId: string): boolean =>
	draggableId.startsWith(DIVIDER_DRAGGABLE_ID_PREFIX);

export const getChangedIssues = <T extends { readonly parentId: number | null }>(
	selectedIssues: T[],
	parentId: number | null,
): T[] => selectedIssues.filter((issue) => issue.parentId !== parentId);

export const handleMutateEpicSuccess = (
	analyticsEvent: UIAnalyticsEvent,
	dndOperation: DndOperation,
	numIssues: number,
) => {
	fireTrackAnalytics(analyticsEvent, 'issueParent updated', {
		analyticsSource: ANALYTICS_SOURCE_EPIC_PANEL,
		issueHierarchyLevel: ISSUE_HIERARCHY_LEVEL_BASE,
		operation: dndOperation,
		numIssues,
	});
};

interface UseDropToAssignEpicParams {
	ref: MutableRefObject<HTMLDivElement | null>;
	epicId: number;
	boardId: number;
	isCompanyManaged: boolean;
	selectedIssues: SelectedIssue[];
	isDropAllowed: () => boolean;
	toggleMessage: (isAssign: boolean, isMessageUsed: boolean) => void;
	onAssignEpic: (
		cards: { id: number }[],
		parentId: number | null,
		requestPromise: Promise<undefined>,
	) => void;
}

/**
 * VISIBLE FOR TESTING
 */
export function useOnDrop({
	epicId,
	boardId,
	selectedIssues,
	isDropAllowed,
	isCompanyManaged,
	toggleMessage,
	onAssignEpic,
	setIsDraggingOver,
}: Pick<
	UseDropToAssignEpicParams,
	| 'epicId'
	| 'boardId'
	| 'selectedIssues'
	| 'isDropAllowed'
	| 'onAssignEpic'
	| 'toggleMessage'
	| 'isCompanyManaged'
> & {
	setIsDraggingOver: (isDraggingOver: boolean) => void;
}): (data: Record<string, unknown>) => void {
	const isAssign = epicId !== UNASSIGNED_EPIC_ID;

	const [assignEpic] = useAssignEpicMutation(boardId);
	const [unassignEpic] = useUnassignEpicMutation(boardId);
	const [updateEpicParent] = useUpdateEpicParentMutation(boardId);

	const { showFlag } = useFlagsService();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const assignTracker = useDropToAssignExperienceTracker({ isCompanyManaged, epicId });
	const updateTracker = useUpdateEpicPanelExperienceTracker({
		isClassic: isCompanyManaged,
		isAssign,
	});
	const { fireExperienceStart, fireExperienceError, fireExperienceSuccess } = fg(
		'add-metric-backlog-panel-update-epic-parent',
	)
		? updateTracker
		: assignTracker;
	const {
		setStartInteraction,
		setStopInteraction,
		setCancelInteraction,
		dndOperation,
		errorLogId,
		errorLogMessage,
	} = fg('add-metric-backlog-panel-update-epic-parent')
		? {
				setStartInteraction: setBacklogPanelEpicUpdateInteractionStartMark,
				setStopInteraction: stopBacklogPanelEpicUpdateInteraction,
				setCancelInteraction: cancelBacklogPanelEpicUpdateInteraction,
				dndOperation: DND_OPERATION_CARD_PARENT_UPDATE,
				errorLogId: 'epic-update.failure',
				errorLogMessage: 'Failed to update an epic',
			}
		: getDropToAssignMarks({ epicId });
	const { formatMessage } = useIntl();

	const onDrop = useEvent((data: Record<string, unknown>) => {
		setIsDraggingOver(false);
		if (!isDropAllowed()) {
			showFlag({
				messageId:
					'software-backlog-panel.ui.epic.epic-panel.epic-card-list.epic-card.use-drop-to-assign-epic.show-flag.warning.ssign-subtask-to-epic-error',
				messageType: 'transactional',
				type: 'warning',
				title: formatMessage(messages.assignSubtaskToEpicErrorFlagTitle),
				description: formatMessage(
					expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
						? messages.assignSubtaskToEpicErrorDescriptionIssueTermRefresh
						: messages.assignSubtaskToEpicErrorDescription,
				),
				isAutoDismiss: true,
			});
			return;
		}

		const changedIssues = fg('jsw_backlog_pragmatic_dnd_automatic_batching')
			? uniqBy([...getChangedIssues(selectedIssues, epicId), { id: Number(data.cardId) }], 'id')
			: getChangedIssues(selectedIssues, epicId);

		if (isDraggableDivider(String(data.type)) || !changedIssues.length) {
			return;
		}

		const isMessageUsed = data.cardParentId !== null;

		toggleMessage(true, isMessageUsed);

		let mutateEpicPromise;

		if (fg('jira-epic-panel-unify-epic-mutations')) {
			mutateEpicPromise = updateEpicParent(changedIssues, epicId);
		} else {
			mutateEpicPromise = isAssign
				? assignEpic(changedIssues, epicId)
				: unassignEpic(changedIssues);
		}

		if (changedIssues.length === 1) {
			const firstSelectedIssue = selectedIssues[0];
			getUpdateAnalyticsFlowHelper().fireAnalyticsEnd(DRAG_DROP_UNKNOWN_FIELD_ID, {
				analytics: createAnalyticsEvent({}),
				attributes: {
					fieldType: FIELD_TYPE_MAP[PARENT_TYPE],
					fieldKey: PARENT_TYPE,
					isDragEditing: true,
					oldValId: firstSelectedIssue.parentId || undefined,
					newValId: epicId === UNASSIGNED_EPIC_ID ? null : epicId,
					...(fg('one_event_improvements_1') && {
						issueId: firstSelectedIssue.id || undefined,
					}),
				},
			});
		}

		setStartInteraction();
		fireExperienceStart();

		const analyticsEvent = createAnalyticsEvent({
			action: 'droppedIn',
			actionSubject: 'card',
		});

		mutateEpicPromise
			.then(() => {
				setStopInteraction(isCompanyManaged);
				fireExperienceSuccess();
				handleMutateEpicSuccess(analyticsEvent, dndOperation, changedIssues.length);
				onAssignEpic(
					changedIssues,
					epicId,
					mutateEpicPromise.then(() => undefined),
				);
			})
			.catch((error: ApolloError) => {
				setCancelInteraction();
				fireExperienceError(error);
				if (error.graphQLErrors?.length) {
					showFlag({
						messageId:
							'software-backlog-panel.ui.epic.epic-panel.epic-card-list.epic-card.use-drop-to-assign-epic.show-flag.error.generic-assign-to-epic-error',
						messageType: 'transactional',
						type: 'error',
						title: formatMessage(
							expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
								? messages.genericAssignToEpicErrorFlagTitleIssueTermRefresh
								: messages.genericAssignToEpicErrorFlagTitle,
						),
						description: error.graphQLErrors[0].message,
					});
				}

				toggleMessage(false, isMessageUsed);
				fireOperationalAnalytics(analyticsEvent, 'cards moveFailed', {
					numCards: selectedIssues.length,
					errorMessage: error.message,
					operation: dndOperation,
					isClientFetchError: isClientFetchError(error),
					analyticsSource: ANALYTICS_SOURCE_EPIC_PANEL,
					issueHierarchyLevel: ISSUE_HIERARCHY_LEVEL_BASE,
				});
				log.safeErrorWithoutCustomerData(errorLogId, errorLogMessage, error);
			});
	});
	return onDrop;
}

export function useDropToAssignEpic({
	ref,
	epicId,
	isDropAllowed,
	boardId,
	selectedIssues,
	isCompanyManaged,
	toggleMessage,
	onAssignEpic,
}: UseDropToAssignEpicParams) {
	const [isDraggingOver, setIsDraggingOver] = useState(false);
	const onDrop = useOnDrop({
		isDropAllowed,
		epicId,
		setIsDraggingOver,
		boardId,
		selectedIssues,
		isCompanyManaged,
		toggleMessage,
		onAssignEpic,
	});

	const autobatch = useAutobatch();
	useEffect(() => {
		if (!ref.current) return undefined;

		return dropTargetForElements({
			element: ref.current,
			canDrop: (args) => isDropAllowed() && args.source.data.type === 'CARD_DRAGGABLE_ID',
			onDragEnter() {
				autobatch(() => {
					setIsDraggingOver(true);
				});
			},
			onDragLeave() {
				autobatch(() => {
					setIsDraggingOver(false);
				});
			},
			onDrop({ source: { data } }) {
				autobatch(() => {
					onDrop(data);
				});
			},
		});
	}, [autobatch, isDropAllowed, onDrop, ref]);

	return {
		isDraggingOver,
	};
}
